import React, { useEffect, useMemo, useState } from 'react';
import { FaCheck, FaLeaf, FaUtensils } from 'react-icons/fa';
import {
  EXTENDED_MEAL_SLOTS,
  GENERAL_REGION_KEYS,
  resolveSlotSelectionPricing,
} from '../../data/specialtyMealPlans';
import { REGION_LABELS } from '../../data/specialtyCatalogApi';
import PlanChooseCard from './PlanChooseCard';
import { formatPlanPriceWhole } from './planPricingUtils';
import './SpecialtyPlans.css';

const FORM_ID = 'sp-select-plan-form';
const DEFAULT_SLOT_IDS = ['lunch'];

const SLOT_UI = {
  breakfast: { label: 'Breakfast', time: '7:00-8:00 am' },
  mid_morning: { label: 'Mid snack', time: '10:30-11:00 am' },
  lunch: { label: 'Lunch', time: '12:45-1:30 pm' },
  evening_snack: { label: 'Eve snack', time: '5:00-6:00 pm' },
  dinner: { label: 'Dinner', time: '8:00-9:00 pm' },
};

export { FORM_ID };

export default function SelectPlanForm({
  plan,
  onConfirm,
  initialOrganic = null,
  initialSlotIds = null,
  initialRegionKey = 'south',
  onStateChange,
}) {
  const isGeneral = plan?.id === 'general' && plan?.regionalMenus;
  const [regionKey, setRegionKey] = useState(initialRegionKey);
  const [selectedSlotIds, setSelectedSlotIds] = useState(
    initialSlotIds?.length ? [...initialSlotIds] : [...DEFAULT_SLOT_IDS],
  );
  const [choice, setChoice] = useState(
    initialOrganic === true ? 'organic' : 'regular',
  );

  useEffect(() => {
    setRegionKey(initialRegionKey);
    setSelectedSlotIds(initialSlotIds?.length ? [...initialSlotIds] : [...DEFAULT_SLOT_IDS]);
    if (initialOrganic === true) setChoice('organic');
    else setChoice('regular');
  }, [plan?.id, initialOrganic, initialSlotIds, initialRegionKey]);

  const pricing = useMemo(
    () => resolveSlotSelectionPricing(plan, selectedSlotIds),
    [plan, selectedSlotIds],
  );

  const organic = choice === 'organic';
  const estimatedPrice = organic ? pricing.chargeOrganic : pricing.chargeRegular;
  const selectedPricing = organic ? pricing.organic : pricing.regular;
  const selectedSavings = selectedPricing?.showPackageDeal && selectedPricing.slotsTotal != null && estimatedPrice != null
    ? selectedPricing.slotsTotal - estimatedPrice
    : 0;
  const canSubmit = Boolean(choice && selectedSlotIds.length > 0 && estimatedPrice != null);

  useEffect(() => {
    onStateChange?.({
      canSubmit,
      estimatedPrice,
      choice,
      slotCount: selectedSlotIds.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSubmit, estimatedPrice, choice, selectedSlotIds.length]);

  const toggleSlot = (slotId) => {
    setSelectedSlotIds((prev) => {
      const next = prev.includes(slotId)
        ? prev.filter((id) => id !== slotId)
        : [...prev, slotId];
      return next.length ? next : prev;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit || !onConfirm) return;
    onConfirm({
      organic: choice === 'organic',
      slotIds: [...selectedSlotIds],
      regionKey: isGeneral ? regionKey : undefined,
    });
  };

  return (
    <form
      id={FORM_ID}
      className={`sp-select-form sp-select-form--v2 sp-select-form--${organic ? 'organic' : 'regular'}`}
      onSubmit={handleSubmit}
    >
      {isGeneral ? (
        <div className="sp-select-form__region-bar">
          {GENERAL_REGION_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`sp-select-region-pill${regionKey === key ? ' sp-select-region-pill--active' : ''}`}
              onClick={() => setRegionKey(key)}
            >
              {plan.regionalMenus[key]?.label?.replace(/\s+Meal$/i, '')
                || REGION_LABELS[key]
                || key}
            </button>
          ))}
        </div>
      ) : null}

      <section className="sp-select-form__block">
        <div className="sp-select-form__section-head">
          <h3 className="sp-select-form__label">Meal type</h3>
        </div>
        <div className="sp-plan-choose-row" role="radiogroup" aria-label="Meal type">
          <PlanChooseCard
            title="Regular"
            description="Standard balanced meals from our kitchen"
            icon={FaUtensils}
            variant="regular"
            pricing={pricing.regular}
            selected={choice === 'regular'}
            onSelect={() => setChoice('regular')}
          />
          <PlanChooseCard
            title="Organic"
            description="Certified organic, farm-to-plate sourcing"
            icon={FaLeaf}
            variant="organic"
            pricing={pricing.organic}
            selected={choice === 'organic'}
            onSelect={() => setChoice('organic')}
          />
        </div>
      </section>

      <section className="sp-select-form__block">
        <div className="sp-select-form__section-head">
          <h3 className="sp-select-form__label">Delivery slots</h3>
        </div>
        <div className="sp-select-slots-row">
          {EXTENDED_MEAL_SLOTS.map((slot) => {
            const active = selectedSlotIds.includes(slot.id);
            const ui = SLOT_UI[slot.id] || { label: slot.label, time: slot.time };
            const slotPrice = pricing.slotPrices[slot.id];
            const displayPrice = choice === 'organic' ? slotPrice?.organic : slotPrice?.regular;
            return (
              <button
                key={slot.id}
                type="button"
                className={`sp-select-slot-v2${active ? ' sp-select-slot-v2--active' : ''}`}
                onClick={() => toggleSlot(slot.id)}
                aria-pressed={active}
              >
                <span className="sp-select-slot-v2__check" aria-hidden>
                  <FaCheck />
                </span>
                {slot.icon ? <slot.icon className="sp-select-slot-v2__icon" aria-hidden /> : null}
                <span className="sp-select-slot-v2__name">{ui.label}</span>
                <span className="sp-select-slot-v2__time">{ui.time}</span>
                <span className="sp-select-slot-v2__prices">
                  {displayPrice != null ? formatPlanPriceWhole(displayPrice) : '-'}
                </span>
              </button>
            );
          })}
        </div>
        <div className="sp-select-form__slot-summary">
          <span className={selectedSavings > 0 ? 'sp-select-saving-pill' : 'sp-select-saving-pill sp-select-saving-pill--muted'}>
            {selectedSavings > 0 ? `Saving ${formatPlanPriceWhole(selectedSavings)}/day` : 'Best price applied'}
          </span>
          <span>{selectedSlotIds.length} slots selected</span>
        </div>
      </section>

      {choice && estimatedPrice == null ? (
        <p className="sp-select-form__warn">Pricing unavailable for this combination. Adjust your slots.</p>
      ) : null}
    </form>
  );
}
